import {
  ApolloClient,
  ApolloLink,
  fromPromise,
  gql,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { B2C_CONTEXT, B2C_KEY, B2C_REFERRER, B2C_URL } from "../credentials";
import { APIErrorResponse, B2CGraphQLErrorType } from "../interfaces/api";
import { getLabel } from "../hooks/use-labels";
import { GraphQLFormattedError } from "graphql";
import { SIGNATURE_EXPIRE_ERROR_CODE, TOKEN_DECODE_ERROR_CODE } from "./configs";
import { logoutOnError, refreshAuthToken } from "./axios";

type GraphqlErrorType = readonly GraphQLFormattedError[] | B2CGraphQLErrorType | undefined;

const loginAgainError = getLabel("loginAgainError");

const headers = () => {
  const user = localStorage.getItem("activeUser");
  const headers: any = {
    "x-api-key": B2C_KEY(),
    "x-context-id": B2C_CONTEXT(),
    "x-referer": B2C_REFERRER(),
  };

  if (user && JSON.parse(user).access_token) {
    headers.authorization = `Bearer ${JSON.parse(user).access_token}`;
  }
  return headers;
};

const authLink = setContext(async (_operation, _previousContext) => ({
  headers: {
    ...headers(),
  },
}));

const isNetworkError = ({
  graphQLErrors,
  networkError = {},
}: {
  graphQLErrors: GraphqlErrorType;
  networkError: any;
}) => {
  const status: number = networkError && networkError?.statusCode ? networkError?.statusCode : null;

  if (graphQLErrors) {
    if (Array.isArray(graphQLErrors)) {
      return (
        graphQLErrors?.some((err) => err.extensions?.code === "UNAUTHENTICATED") || status === 403
      );
    } else {
      const { http_status = 200, response_code = "" } = graphQLErrors as B2CGraphQLErrorType;
      return http_status === 403 && response_code === SIGNATURE_EXPIRE_ERROR_CODE;
    }
  }
  return false;
};

const isTokenValid = (graphQLErrors: GraphqlErrorType) => {
  return !(
    graphQLErrors &&
    "response_code" in graphQLErrors &&
    graphQLErrors?.response_code === TOKEN_DECODE_ERROR_CODE
  );
};

const errorLink = onError(({ graphQLErrors, networkError = {} as any, operation, forward }) => {
  if (!graphQLErrors) return;

  if (!isTokenValid(graphQLErrors)) {
    logoutOnError(loginAgainError);
    return;
  } else if (isNetworkError({ networkError, graphQLErrors })) {
    return fromPromise(
      refreshAuthToken()
        .then((activeUser) => {
          return activeUser ?? {};
        })
        .catch((err) => {
          console.error(err);
          throw err;
        }),
    ).flatMap((accessToken) => {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          authorization: `Bearer ${accessToken?.access_token}`,
        },
      }));
      return forward(operation);
    });
  } else {
    console.error(graphQLErrors);
    return;
  }
});

const cache = new InMemoryCache();

const getApolloClient = () =>
  new ApolloClient({
    link: ApolloLink.from([authLink, errorLink, new HttpLink({ uri: B2C_URL() })]),
    uri: B2C_URL(),
    cache,
    headers: headers(),

    defaultOptions: {
      watchQuery: {
        fetchPolicy: "network-only",
      },
      query: {
        fetchPolicy: "no-cache",
      },
    },
  });

const extractGraphQLError = (errorResponse: any) => {
  const errorObject = {} as APIErrorResponse;

  const { graphQLErrors, networkError } = errorResponse;

  if (graphQLErrors && graphQLErrors.length) {
    const { message, extensions } = graphQLErrors[0];
    const msg = typeof message === "string" ? message : "";

    if (extensions && extensions.response_code) {
      const { response_code, message, fieldErrors } = extensions;
      errorObject.error_code = response_code;
      errorObject.message = message;
      errorObject.field_errors = fieldErrors;
      return errorObject;
    }

    if (extensions && extensions.errors) {
      const { errors } = extensions;
      errorObject.error_code = errors.response_code;
      errorObject.message = errors.message ?? errors.title;
      errorObject.field_errors = errors.field_error;
      return errorObject;
    }

    if (extensions && extensions.status === 401) {
      errorObject.error_code = extensions?.error ?? "UNAUTHORIZED";
      errorObject.message = getLabel("unauthorizedActionMessage");
      errorObject.field_errors = extensions?.error ?? "";

      return errorObject;
    }

    if (extensions && (extensions.status || extensions.error)) {
      errorObject.error_code = String(extensions.status || extensions.error);
      // prefer extensions.message if non-empty, else fall back to extensions.error
      errorObject.message = extensions.message || extensions.error || msg;
      return errorObject;
    }

    if (message) {
      errorObject.message = message;
      return errorObject;
    }
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);

    if (networkError.statusCode === 403) {
      const apiErrors = networkError?.result?.errors;
      const notice =
        apiErrors?.message ||
        networkError?.result?.errors?.title ||
        getLabel("accessDeniedErrorLabel");
      logoutOnError(notice);
      return null;
    }

    errorObject.error_code = "NETWORK_ERROR";
    errorObject.message = "Unable to reach the server";
    return errorObject;
  }

  return null;
};

const handleGraphQLError = (error: any) => {
  const graphqlError = extractGraphQLError(error);

  if (graphqlError) {
    return Promise.reject(graphqlError);
  }

  return Promise.reject({
    errorCode: "GENERAL",
    message: "Something went wrong",
  });
};

export const query = async (queryStr: string, variables?: any) => {
  return getApolloClient()
    .query({
      query: gql`
        ${queryStr}
      `,
      variables,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => handleGraphQLError(error));
};

export const mutation = async (mutationStr: string, variables?: any) => {
  return getApolloClient()
    .mutate({
      mutation: gql`
        ${mutationStr}
      `,
      variables,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => handleGraphQLError(error));
};
